import React, { useRef, useEffect, useCallback, useState } from 'react';
import styled from 'styled-components';
import { Bold, Italic, Underline, Strikethrough, Link, Image, List, ListOrdered, Quote, Code } from 'lucide-react';

const PlanningWrap = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #202020;
  border-radius: 0.5rem;
  overflow: hidden;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.5rem 1rem;
  border-bottom: 1px solid #383836;
  background: #202020;
  flex-shrink: 0;
`;

const ToolbarBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  padding: 0.25rem;
  border: none;
  border-radius: 0.375rem;
  background: transparent;
  color: #9ca3af;
  cursor: pointer;
  transition: color 0.15s, background 0.15s;

  &:hover {
    color: #e5e7eb;
    background: #383836;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const ToolbarSelect = styled.select`
  height: 2.25rem;
  width: 6.5rem;
  padding: 0.25rem 2rem 0.25rem 0.5rem;
  margin-right: 0.25rem;
  border: none;
  border-radius: 0.375rem;
  background-color: #2d2d2d;
  color: #e5e7eb;
  font-size: 0.8125rem;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.5rem center;
  background-size: 0.75rem;
  box-sizing: border-box;

  &:hover {
    background-color: #383836;
  }
`;

const EditorArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
`;

const Editable = styled.div`
  min-height: 100%;
  padding: 1rem 1.5rem;
  outline: none;
  color: #e5e7eb;
  font-size: 1rem;
  line-height: 1.6;

  &[contenteditable]:empty::before {
    content: attr(data-placeholder);
    color: #6b7280;
  }

  a {
    color: #60a5fa;
  }
  img {
    max-width: 100%;
    height: auto;
    border-radius: 0.25rem;
  }
  blockquote {
    border-left: 4px solid #52525b;
    margin: 0.5rem 0;
    padding-left: 1rem;
    color: #9ca3af;
  }
  code {
    background: #2d2d2d;
    padding: 0.125rem 0.375rem;
    border-radius: 0.25rem;
    font-size: 0.9em;
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

interface PlanningViewProps {
  value: string;
  onChange: (value: string) => void;
}

const SIZE_OPTIONS = [
  { value: '16px', label: 'Normal' },
  { value: '12px', label: 'Small' },
  { value: '18px', label: 'Large' },
  { value: '22px', label: 'Larger' },
] as const;

function getSizeAtSelection(editorEl: HTMLDivElement | null): string {
  if (!editorEl) return '16px';
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return '16px';
  const node = sel.focusNode;
  if (!node || !editorEl.contains(node)) return '16px';
  const el: Element | null = node.nodeType === Node.TEXT_NODE ? node.parentElement : (node as Element);
  if (!el) return '16px';
  const px = parseFloat(getComputedStyle(el).fontSize);
  if (px <= 13) return '12px';
  if (px <= 17) return '16px';
  if (px <= 20) return '18px';
  if (px <= 24) return '22px';
  return '16px';
}

const PlanningView: React.FC<PlanningViewProps> = ({ value, onChange }) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const initialSynced = useRef(false);
  const [selectedSize, setSelectedSize] = useState<string>('16px');

  useEffect(() => {
    const el = editorRef.current;
    if (!el || initialSynced.current) return;
    el.innerHTML = value || '';
    initialSynced.current = true;
  }, [value]);

  const syncContent = useCallback(() => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);

  const exec = useCallback(
    (cmd: string, value?: string) => {
      editorRef.current?.focus();
      document.execCommand(cmd, false, value ?? undefined);
      syncContent();
    },
    [syncContent]
  );

  const applyFontSize = useCallback(
    (size: string) => {
      const el = editorRef.current;
      if (!el) return;
      el.focus();
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return;
      const range = sel.getRangeAt(0);
      const span = document.createElement('span');
      span.style.fontSize = size;
      try {
        if (range.collapsed) {
          span.appendChild(document.createTextNode('\u200B'));
          range.insertNode(span);
          range.setStart(span.firstChild!, 1);
          range.setEnd(span.firstChild!, 1);
          sel.removeAllRanges();
          sel.addRange(range);
        } else {
          const contents = range.extractContents();
          span.appendChild(contents);
          range.insertNode(span);
        }
        syncContent();
      } catch {
        // fallback: insert at cursor
        span.appendChild(document.createTextNode('\u200B'));
        range.insertNode(span);
        range.setStart(span.firstChild!, 1);
        range.setEnd(span.firstChild!, 1);
        sel.removeAllRanges();
        sel.addRange(range);
        syncContent();
      }
      setSelectedSize(size);
    },
    [syncContent]
  );

  const handleInput = useCallback(() => {
    syncContent();
  }, [syncContent]);

  const updateSizeFromSelection = useCallback(() => {
    setSelectedSize(getSizeAtSelection(editorRef.current));
  }, []);

  const handleLink = useCallback(() => {
    const url = prompt('Enter URL:');
    if (url == null) return;
    if (url) {
      exec('createLink', url.startsWith('http') ? url : `https://${url}`);
    } else {
      exec('unlink');
    }
  }, [exec]);

  const handleImage = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleImageSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file || !file.type.startsWith('image/')) return;
      const reader = new FileReader();
      reader.onload = () => {
        const url = reader.result as string;
        const el = editorRef.current;
        if (!el) return;
        el.focus();
        const sel = window.getSelection();
        if (sel && sel.rangeCount) {
          const range = sel.getRangeAt(0);
          const img = document.createElement('img');
          img.src = url;
          img.alt = 'Uploaded';
          range.deleteContents();
          range.insertNode(img);
          range.collapse(false);
          sel.removeAllRanges();
          sel.addRange(range);
        } else {
          const img = document.createElement('img');
          img.src = url;
          img.alt = 'Uploaded';
          el.appendChild(img);
        }
        syncContent();
        e.target.value = '';
      };
      reader.readAsDataURL(file);
    },
    [syncContent]
  );

  return (
    <PlanningWrap>
      <Toolbar>
        <ToolbarSelect
          title="Text size"
          value={selectedSize}
          onChange={(e) => {
            const v = e.target.value;
            applyFontSize(v);
          }}
        >
          {SIZE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </ToolbarSelect>
        <ToolbarBtn type="button" onClick={() => exec('bold')} title="Bold">
          <Bold size={18} />
        </ToolbarBtn>
        <ToolbarBtn type="button" onClick={() => exec('italic')} title="Italic">
          <Italic size={18} />
        </ToolbarBtn>
        <ToolbarBtn type="button" onClick={() => exec('underline')} title="Underline">
          <Underline size={18} />
        </ToolbarBtn>
        <ToolbarBtn type="button" onClick={() => exec('strikeThrough')} title="Strikethrough">
          <Strikethrough size={18} />
        </ToolbarBtn>
        <ToolbarBtn type="button" onClick={handleLink} title="Link">
          <Link size={18} />
        </ToolbarBtn>
        <ToolbarBtn type="button" onClick={handleImage} title="Insert image">
          <Image size={18} />
        </ToolbarBtn>
        <ToolbarBtn type="button" onClick={() => exec('insertUnorderedList')} title="Bullet list">
          <List size={18} />
        </ToolbarBtn>
        <ToolbarBtn type="button" onClick={() => exec('insertOrderedList')} title="Numbered list">
          <ListOrdered size={18} />
        </ToolbarBtn>
        <ToolbarBtn type="button" onClick={() => exec('formatBlock', 'blockquote')} title="Quote">
          <Quote size={18} />
        </ToolbarBtn>
        <ToolbarBtn type="button" onClick={() => exec('formatBlock', 'pre')} title="Code block">
          <Code size={18} />
        </ToolbarBtn>
      </Toolbar>
      <HiddenFileInput
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleImageSelect}
      />
      <EditorArea>
        <Editable
          ref={editorRef}
          contentEditable
          data-placeholder="Write your trip notes, ideas, and plans…"
          onInput={handleInput}
          onMouseUp={updateSizeFromSelection}
          onKeyUp={updateSizeFromSelection}
          onPaste={(e) => {
            const items = e.clipboardData?.items;
            if (!items) return;
            for (let i = 0; i < items.length; i++) {
              const item = items[i];
              if (item.type.startsWith('image/')) {
                e.preventDefault();
                const file = item.getAsFile();
                if (!file) return;
                const reader = new FileReader();
                reader.onload = () => {
                  const url = reader.result as string;
                  const sel = window.getSelection();
                  if (sel && sel.rangeCount && editorRef.current) {
                    const range = sel.getRangeAt(0);
                    const img = document.createElement('img');
                    img.src = url;
                    img.alt = 'Pasted';
                    range.deleteContents();
                    range.insertNode(img);
                    range.collapse(false);
                    sel.removeAllRanges();
                    sel.addRange(range);
                    syncContent();
                  }
                };
                reader.readAsDataURL(file);
                return;
              }
            }
          }}
        />
      </EditorArea>
    </PlanningWrap>
  );
};

export default PlanningView;
